import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import BACKEND_URL from '../config';
import SEO from '../components/SEO';
import { useAuthModal } from '../context/AuthModalContext';
import Navbar from '../components/Navbar';

const API = BACKEND_URL;

function getApex30Window() {
  const now = new Date();
  const year = now.getFullYear();
  const start = new Date(year, 6, 1);
  const end   = new Date(year, 6, 30);
  return { start, end };
}

function getCurrentDay() {
  const { start } = getApex30Window();
  const now = new Date();
  const diff = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return Math.max(0, Math.min(30, diff + 1));
}

// Returns how many days are unlocked (catch-up: all days up to today)
function getUnlockedUpTo() {
  const { start } = getApex30Window();
  const now = new Date();
  if (now < start) return 0; // before July 1 — nothing unlocked yet
  const diff = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return Math.min(30, diff + 1); // +1 so Day 1 is unlocked on July 1
}

function getDaysUntilStart() {
  const { start } = getApex30Window();
  const now = new Date();
  const diff = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function isEventActive() {
  const { start, end } = getApex30Window();
  const now = new Date();
  return now >= start && now <= end;
}

const MOCK_LEADERBOARD = [
  { rank: 1, name: 'Priya S.',   days: 18, streak: 18, badge: '🔥' },
  { rank: 2, name: 'Marcus T.',  days: 16, streak: 14, badge: '⚡' },
  { rank: 3, name: 'Aisha K.',   days: 15, streak: 15, badge: '🌟' },
  { rank: 4, name: 'James L.',   days: 13, streak:  9, badge: ''   },
  { rank: 5, name: 'Neha R.',    days: 12, streak: 12, badge: ''   },
  { rank: 6, name: 'Carlos M.',  days: 10, streak:  7, badge: ''   },
  { rank: 7, name: 'Yuki W.',    days:  9, streak:  4, badge: ''   },
  { rank: 8, name: 'Sofia B.',   days:  8, streak:  8, badge: ''   },
];

const WEEK_THEMES = [
  { week: 1, days: [1,7],   icon: '🏢', title: 'Onboarding at Cloudify',  color: '#10b981', desc: 'String formatting, Maps, SOQL, Before Triggers' },
  { week: 2, days: [8,14],  icon: '⚙️', title: 'Automation Sprint',        color: '#4f8ef7', desc: 'After Triggers, Batch, Queueable, Schedulable' },
  { week: 3, days: [15,21], icon: '🔗', title: 'Integration Week',          color: '#f59e0b', desc: 'HTTP Callouts, Platform Events, Caching, Mocks' },
  { week: 4, days: [22,30], icon: '🚀', title: 'Scale & Quality',           color: '#c084fc', desc: 'Validation, Schema, Error Budgets, Capstone'   },
];

const DIFF_STYLE = {
  Easy:   { bg: 'rgba(16,185,129,0.12)',  color: '#10b981', border: 'rgba(16,185,129,0.3)'  },
  Medium: { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b', border: 'rgba(245,158,11,0.3)'  },
  Hard:   { bg: 'rgba(248,113,113,0.12)', color: '#f87171', border: 'rgba(248,113,113,0.3)' },
};

function Countdown({ c }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  useEffect(() => {
    const calc = () => {
      const { start, end } = getApex30Window();
      const now = new Date();
      const target = now < start ? start : end;
      const diff = Math.max(0, target - now);
      return { days: Math.floor(diff / (1000*60*60*24)), hours: Math.floor((diff / (1000*60*60)) % 24), mins: Math.floor((diff / (1000*60)) % 60), secs: Math.floor((diff / 1000) % 60) };
    };
    setTimeLeft(calc());
    const t = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
      {[{ label: 'Days', val: timeLeft.days }, { label: 'Hours', val: timeLeft.hours }, { label: 'Minutes', val: timeLeft.mins }, { label: 'Seconds', val: timeLeft.secs }].map(({ label, val }) => (
        <div key={label} style={{ textAlign: 'center', minWidth: 64 }}>
          <div style={{ fontSize: 36, fontWeight: 800, color: c.accent, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{String(val).padStart(2, '0')}</div>
          <div style={{ fontSize: 11, color: c.secondary, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

function DayGrid({ challenges, completedIds, currentDay, unlockedUpTo, c, onDayClick, joined }) {
  if (!challenges.length) return <div style={{ textAlign: 'center', padding: '40px 0', color: c.secondary }}>Loading challenges…</div>;
  const weekColors = ['#10b981', '#4f8ef7', '#f59e0b', '#c084fc'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8 }}>
      {challenges.map((ch) => {
        const day = ch.order_index;
        const done = completedIds.has(ch.id);
        const isToday = day === currentDay && isEventActive();
        const locked = joined && isEventActive() && day > unlockedUpTo;
        const weekIdx = day <= 7 ? 0 : day <= 14 ? 1 : day <= 21 ? 2 : 3;
        const weekColor = weekColors[weekIdx];
        let bg, border, textColor;
        if (done) { bg = 'rgba(16,185,129,0.18)'; border = '#10b981'; textColor = '#10b981'; }
        else if (isToday) { bg = `${weekColor}22`; border = weekColor; textColor = weekColor; }
        else if (locked) { bg = c.bgAlt || c.card; border = c.border; textColor = c.muted || c.secondary; }
        else { bg = c.card; border = c.border; textColor = c.text; }

        // Compute unlock date label for locked days
        const unlockDate = locked ? (() => {
          const { start } = getApex30Window();
          const d = new Date(start);
          d.setDate(d.getDate() + day - 1);
          return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;
        })() : null;

        return (
          <div key={ch.id}
            onClick={() => joined && !locked && onDayClick(ch)}
            title={locked ? `Unlocks ${unlockDate}` : undefined}
            style={{
              position: 'relative', background: bg, border: `1.5px solid ${border}`,
              borderRadius: 10, padding: '10px 6px', textAlign: 'center',
              cursor: locked ? 'not-allowed' : (joined ? 'pointer' : 'default'),
              transition: 'all 0.15s',
              opacity: !joined && !done ? 0.5 : 1,
              overflow: 'hidden',
            }}>
            {/* Blur overlay for locked days */}
            {locked && (
              <div style={{
                position: 'absolute', inset: 0, backdropFilter: 'blur(3px)',
                background: 'rgba(0,0,0,0.35)', borderRadius: 8,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 2, zIndex: 2,
              }}>
                <span style={{ fontSize: 14 }}>🔒</span>
                <span style={{ fontSize: 8, color: '#9aa1b0', fontWeight: 600, letterSpacing: '0.04em' }}>{unlockDate}</span>
              </div>
            )}
            <div style={{ fontSize: 18, marginBottom: 4 }}>{done ? '✓' : isToday ? '▶' : day}</div>
            <div style={{ fontSize: 9, color: textColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Day {day}</div>
          </div>
        );
      })}
    </div>
  );
}

function WeekBreakdown({ c }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
      {WEEK_THEMES.map(({ week, days, icon, title, color, desc }) => (
        <div key={week} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: '16px 18px', borderLeft: `3px solid ${color}` }}>
          <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 4 }}>Week {week} — {title}</div>
          <div style={{ fontSize: 11, color: c.secondary, marginBottom: 6 }}>Days {days[0]}–{days[1]}</div>
          <div style={{ fontSize: 11, color: c.secondary }}>{desc}</div>
        </div>
      ))}
    </div>
  );
}

function Leaderboard({ c, userRank }) {
  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${c.border}`, fontWeight: 700, color: c.text, fontSize: 14 }}>🏆 Apex30 Leaderboard</div>
      {MOCK_LEADERBOARD.map(({ rank, name, days, streak, badge }) => (
        <div key={rank} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderBottom: `1px solid ${c.border}`, background: rank === userRank ? c.accentBg : 'transparent' }}>
          <span style={{ width: 24, fontSize: 13, fontWeight: 700, color: rank <= 3 ? ['#f59e0b','#9aa1b0','#cd7c3a'][rank-1] : c.muted, textAlign: 'center' }}>{rank}</span>
          <span style={{ flex: 1, fontSize: 13, color: c.text, fontWeight: rank === userRank ? 700 : 400 }}>{name} {badge}</span>
          <span style={{ fontSize: 12, color: c.secondary }}>{days} days</span>
          <span style={{ fontSize: 12, color: c.accent }}>🔥{streak}</span>
        </div>
      ))}
    </div>
  );
}

function DayChallengeModal({ challenge, c, onClose, onSolve }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  const diff = DIFF_STYLE[challenge.difficulty] || DIFF_STYLE.Hard;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 16, padding: 28, maxWidth: 520, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: c.accent, fontWeight: 700 }}>Day {challenge.order_index} · Apex30</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: c.text, margin: '0 0 8px' }}>{challenge.title}</h3>
        <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: diff.color, background: diff.bg, border: `1px solid ${diff.border}`, borderRadius: 4, padding: '2px 8px', marginBottom: 14 }}>{challenge.difficulty}</div>
        <p style={{ fontSize: 13, color: c.secondary, lineHeight: 1.7, margin: '0 0 20px' }}>{challenge.description}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '9px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: 8, color: c.secondary, cursor: 'pointer', fontSize: 13 }}>Close</button>
          <button onClick={() => onSolve(challenge)} style={{ flex: 2, padding: '9px 22px', background: c.accent, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Open in Editor →</button>
        </div>
      </div>
    </div>
  );
}

export default function Apex30() {
  const { theme } = useTheme();
  const c = TOKENS[theme];
  const navigate = useNavigate();
  const { user } = useAuth();

  const [challenges, setChallenges]           = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [joined, setJoined]                   = useState(false);
  const [completedIds, setCompletedIds]       = useState(new Set()); // eslint-disable-line no-unused-vars
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [joinLoading, setJoinLoading]         = useState(false);
  const [activeTab, setActiveTab]             = useState('calendar');
  const calendarRef = useRef(null);
  const { openAuthModal } = useAuthModal();
  const currentDay  = getCurrentDay();
  const unlockedUpTo = getUnlockedUpTo();
  const daysUntil   = getDaysUntilStart();
  const eventActive = isEventActive();
  const progress    = challenges.length ? Math.round((completedIds.size / 30) * 100) : 0;

  useEffect(() => {
    const load = async () => {
      try {
        const res  = await fetch(`${API}/challenges?category=apex30`);
        const data = await res.json();
        data.sort((a, b) => a.order_index - b.order_index);
        setChallenges(data);
      } catch (e) {
        console.error('Failed to load apex30 challenges', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (user) {
      const key = `apex30_joined_${user.id}`;
      setJoined(localStorage.getItem(key) === 'true');
    }
  }, [user]);

  const handleJoin = async () => {
    if (!user) { openAuthModal(); return; }
    setJoinLoading(true);
    await new Promise(r => setTimeout(r, 600));
    localStorage.setItem(`apex30_joined_${user.id}`, 'true');
    setJoined(true);
    setJoinLoading(false);
    calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSolve = (ch) => {
    setSelectedChallenge(null);
    navigate(`/challenge/${ch.id}`);
  };

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.style.opacity = 1; e.target.style.transform = 'translateY(0)'; }
      });
    }, { threshold: 0.08 });
    document.querySelectorAll('.a30-reveal').forEach(el => {
      el.style.opacity = 0; el.style.transform = 'translateY(24px)'; el.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
      obs.observe(el);
    });
    return () => obs.disconnect();
  }, [challenges]);

  return (
    <div style={{ minHeight: '100vh', background: c.bg, color: c.text, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <SEO title="Apex 30-Day Challenge" description="30 days of Apex coding challenges. Build real Salesforce skills one day at a time." url="https://trailcode.org/apex30" />

      <Navbar />

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, #0a0f1e 0%, #0d1528 50%, #08090e 100%)`, borderBottom: `1px solid ${c.border}`, padding: '64px 24px 56px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(79,142,247,0.12)', border: '1px solid rgba(79,142,247,0.3)', borderRadius: 20, padding: '6px 16px', marginBottom: 24, fontSize: 12, color: '#7db3fa', fontWeight: 600 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: eventActive ? '#10b981' : '#f59e0b', display: 'inline-block' }} />
            {eventActive ? `Day ${currentDay} of 30 · Event Active` : daysUntil > 0 ? `Starts in ${daysUntil} days · July 2026` : 'Apex30 · 30 challenges, 30 days'}
          </div>
          <h1 style={{ fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 900, color: '#e8eaf0', margin: '0 0 16px', lineHeight: 1.1, letterSpacing: '-0.02em' }}>Apex30 Challenge</h1>
          <p style={{ fontSize: 'clamp(15px, 2.5vw, 20px)', color: '#9aa1b0', maxWidth: 560, margin: '0 auto 32px', lineHeight: 1.6 }}>
            30 days. 30 Apex challenges. One fictional company — <strong style={{ color: '#7db3fa' }}>Cloudify Inc.</strong> — and a story that unfolds as you code.
          </p>
          {!eventActive && daysUntil > 0 ? (
            <div style={{ marginBottom: 36 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Event begins in</div>
              <Countdown c={{ accent: '#4f8ef7', secondary: '#6b7280' }} />
            </div>
          ) : eventActive ? (
            <div style={{ marginBottom: 36 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Time remaining</div>
              <Countdown c={{ accent: '#4f8ef7', secondary: '#6b7280' }} />
            </div>
          ) : null}
          {!joined ? (
            <button onClick={handleJoin} disabled={joinLoading}
              style={{ padding: '14px 36px', background: '#4f8ef7', border: 'none', borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 700, cursor: joinLoading ? 'wait' : 'pointer', boxShadow: '0 4px 24px rgba(79,142,247,0.4)', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#3a7de8'}
              onMouseLeave={e => e.currentTarget.style.background = '#4f8ef7'}>
              {joinLoading ? 'Joining…' : "🔥 Join Apex30 — It's Free"}
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 14, color: '#10b981', fontWeight: 600 }}>✓ You're in! {eventActive ? `Day ${currentDay}/30` : 'Starts July 1'}</div>
              {eventActive && (
                <button onClick={() => { const todayChallenge = challenges.find(ch => ch.order_index === currentDay); if (todayChallenge) setSelectedChallenge(todayChallenge); }}
                  style={{ padding: '12px 28px', background: '#4f8ef7', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  ▶ Today's Challenge — Day {currentDay}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ background: c.bgAlt, borderBottom: `1px solid ${c.border}`, padding: '16px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[{ label: 'Challenges', val: '30' }, { label: 'Story Arc', val: '4 weeks' }, { label: 'Topics Covered', val: '12+' }, { label: 'Difficulty', val: 'Easy → Hard' }, { label: 'Company', val: 'Cloudify Inc.' }].map(({ label, val }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: c.accent }}>{val}</div>
              <div style={{ fontSize: 11, color: c.secondary, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px', display: 'grid', gridTemplateColumns: '1fr 280px', gap: 28, alignItems: 'start' }}>
        <div>
          {joined && (
            <div className="a30-reveal" style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: '20px 24px', marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: c.text, fontSize: 15 }}>Your Progress</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: c.accent }}>{completedIds.size}/30 challenges</div>
              </div>
              <div style={{ background: c.border, borderRadius: 6, height: 8, overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: `linear-gradient(90deg, #4f8ef7, #10b981)`, borderRadius: 6, transition: 'width 0.5s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: c.secondary }}>
                <span>{progress}% complete</span>
                <span>{30 - completedIds.size} remaining</span>
              </div>
            </div>
          )}

          <div className="a30-reveal" style={{ display: 'flex', gap: 4, marginBottom: 20, background: c.bgAlt, border: `1px solid ${c.border}`, borderRadius: 10, padding: 4 }}>
            {[{ key: 'calendar', label: '📅 Calendar' }, { key: 'weeks', label: '📚 Weekly Themes' }, { key: 'leaderboard', label: '🏆 Leaderboard' }].map(({ key, label }) => (
              <button key={key} onClick={() => setActiveTab(key)}
                style={{ flex: 1, padding: '8px 12px', background: activeTab === key ? c.card : 'none', border: activeTab === key ? `1px solid ${c.border}` : '1px solid transparent', borderRadius: 7, color: activeTab === key ? c.text : c.secondary, fontSize: 13, fontWeight: activeTab === key ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s' }}>
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'calendar' && (
            <div className="a30-reveal" ref={calendarRef}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: c.text, fontSize: 15 }}>{joined ? (eventActive ? `Day ${currentDay} of 30` : 'Ready — starts July 1') : '30-Day Calendar'}</div>
                {!joined && <div style={{ fontSize: 12, color: c.secondary }}>Join to unlock challenges</div>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
                {WEEK_THEMES.map(({ week, color, icon }) => (
                  <div key={week} style={{ textAlign: 'center', fontSize: 11, color, fontWeight: 700, padding: '4px 0', borderBottom: `2px solid ${color}` }}>{icon} W{week}</div>
                ))}
              </div>
              {loading ? <div style={{ textAlign: 'center', padding: 40, color: c.secondary }}>Loading…</div> : (
                <DayGrid challenges={challenges} completedIds={completedIds} currentDay={currentDay} unlockedUpTo={unlockedUpTo} c={c} onDayClick={setSelectedChallenge} joined={joined} />
              )}
              <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                {[{ color: '#10b981', label: 'Completed' }, { color: '#4f8ef7', label: "Today's challenge" }, { color: c.border, label: 'Available' }, { color: c.border, label: '🔒 Locked', locked: true }].map(({ color, label, locked: isLocked }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: c.secondary }}>
                    {isLocked
                      ? <span style={{ fontSize: 11 }}>🔒</span>
                      : <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />}
                    {isLocked ? 'Locked (future day)' : label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'weeks' && (
            <div className="a30-reveal">
              <div style={{ fontWeight: 700, color: c.text, fontSize: 15, marginBottom: 16 }}>Weekly Themes</div>
              <WeekBreakdown c={c} />
              {!loading && (
                <div style={{ marginTop: 24 }}>
                  {WEEK_THEMES.map(({ week, days, color, title }) => {
                    const weekChallenges = challenges.filter(ch => ch.order_index >= days[0] && ch.order_index <= days[1]);
                    return (
                      <div key={week} style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 3, height: 16, background: color, borderRadius: 2, display: 'inline-block' }} />
                          Week {week} — {title}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {weekChallenges.map(ch => {
                            const diff = DIFF_STYLE[ch.difficulty] || DIFF_STYLE.Hard;
                            const locked = joined && isEventActive() && ch.order_index > unlockedUpTo;
                            const unlockDate = locked ? (() => {
                              const { start } = getApex30Window();
                              const d = new Date(start);
                              d.setDate(d.getDate() + ch.order_index - 1);
                              return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;
                            })() : null;
                            return (
                              <div key={ch.id}
                                onClick={() => !locked && setSelectedChallenge(ch)}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', cursor: locked ? 'not-allowed' : 'pointer', transition: 'all 0.15s', opacity: locked ? 0.55 : 1, filter: locked ? 'blur(0.4px)' : 'none' }}
                                onMouseEnter={e => { if (!locked) e.currentTarget.style.borderColor = color; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${color}22`, border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color, flexShrink: 0 }}>{locked ? '🔒' : ch.order_index}</div>
                                <div style={{ flex: 1, fontSize: 13, color: locked ? c.secondary : c.text, fontWeight: 500 }}>{locked ? `Day ${ch.order_index} — unlocks ${unlockDate}` : ch.title}</div>
                                {!locked && <div style={{ fontSize: 11, fontWeight: 600, color: diff.color, background: diff.bg, border: `1px solid ${diff.border}`, borderRadius: 4, padding: '2px 8px' }}>{ch.difficulty}</div>}
                                {!locked && <span style={{ fontSize: 12, color }}>→</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div className="a30-reveal">
              <Leaderboard c={c} userRank={joined ? 4 : null} />
              <div style={{ marginTop: 12, fontSize: 12, color: c.secondary, textAlign: 'center' }}>Leaderboard updates daily. Opt-in to appear publicly.</div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!joined && (
            <div className="a30-reveal" style={{ background: 'linear-gradient(135deg, #0d1528, #0f1117)', border: '1px solid rgba(79,142,247,0.3)', borderRadius: 14, padding: '20px' }}>
              <div style={{ fontSize: 22, marginBottom: 10 }}>🔥</div>
              <div style={{ fontWeight: 700, color: '#e8eaf0', fontSize: 15, marginBottom: 8 }}>Join Apex30</div>
              <div style={{ fontSize: 13, color: '#9aa1b0', lineHeight: 1.6, marginBottom: 16 }}>30 challenges across 4 weeks. Build real Apex skills at Cloudify Inc.</div>
              <button onClick={handleJoin} disabled={joinLoading} style={{ width: '100%', padding: '10px', background: '#4f8ef7', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                {joinLoading ? 'Joining…' : 'Join for free →'}
              </button>
            </div>
          )}

          {joined && eventActive && (() => {
            const todayChallenge = challenges.find(ch => ch.order_index === currentDay);
            if (!todayChallenge) return null;
            const diff = DIFF_STYLE[todayChallenge.difficulty] || DIFF_STYLE.Hard;
            return (
              <div className="a30-reveal" style={{ background: c.card, border: `1px solid ${c.accent}`, borderRadius: 14, padding: '18px', boxShadow: `0 0 20px rgba(79,142,247,0.12)` }}>
                <div style={{ fontSize: 11, color: c.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>📅 Today · Day {currentDay}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 10, lineHeight: 1.4 }}>{todayChallenge.title}</div>
                <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: diff.color, background: diff.bg, border: `1px solid ${diff.border}`, borderRadius: 4, padding: '2px 8px', marginBottom: 14 }}>{todayChallenge.difficulty}</div>
                <button onClick={() => setSelectedChallenge(todayChallenge)} style={{ width: '100%', padding: '10px', background: c.accent, border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Start Challenge →</button>
              </div>
            );
          })()}

          {joined && !loading && (
            <div className="a30-reveal" style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: '18px' }}>
              <div style={{ fontWeight: 700, color: c.text, fontSize: 14, marginBottom: 14 }}>Week Progress</div>
              {WEEK_THEMES.map(({ week, days, color, icon }) => {
                const total = days[1] - days[0] + 1;
                const done  = challenges.filter(ch => ch.order_index >= days[0] && ch.order_index <= days[1] && completedIds.has(ch.id)).length;
                const pct   = Math.round((done / total) * 100);
                return (
                  <div key={week} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: c.secondary }}>{icon} Week {week}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color }}>{done}/{total}</span>
                    </div>
                    <div style={{ background: c.border, borderRadius: 4, height: 5, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="a30-reveal" style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: '18px' }}>
            <div style={{ fontWeight: 700, color: c.text, fontSize: 14, marginBottom: 12 }}>🏢 About Cloudify Inc.</div>
            <div style={{ fontSize: 12, color: c.secondary, lineHeight: 1.7 }}>A fictional SaaS company used throughout all 30 challenges. You join as a junior Salesforce dev on Day 1 and ship increasingly complex features across 4 weeks.</div>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {['Real business scenarios', 'Continuous story arc', 'Builds on previous days', 'Capstone on Day 30'].map(item => (
                <div key={item} style={{ fontSize: 12, color: c.text, display: 'flex', gap: 8 }}>
                  <span style={{ color: c.accent }}>✓</span> {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedChallenge && (
        <DayChallengeModal challenge={selectedChallenge} c={c} onClose={() => setSelectedChallenge(null)} onSolve={handleSolve} />
      )}

      <div style={{ borderTop: `1px solid ${c.border}`, padding: '24px', textAlign: 'center', fontSize: 12, color: c.secondary, marginTop: 40 }}>
        TrailCode · Apex30 · July 2026 · Built for Salesforce developers
      </div>
    </div>
  );
}